export const TRIAL_DAYS = 7;
export const EXTENSION_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const getCreatedAt = user => Number.parseInt(user['custom:created_at'] || '0', 10);
const getExtendedDays = user => Number.parseInt(user['custom:extended_trial_days'] || '0', 10);
const getTrialExtendedAt = user => Number.parseInt(user['custom:trial_extended_at'] || '0', 10);

export interface TrialAttributeOverrides {
  createdAt?: string | null;
  extendedTrialDays?: number | null;
  trialExtendedAt?: string | null;
}

export const mergeTrialAttributes = (user, overrides?: TrialAttributeOverrides) => {
  if (!user || !overrides) {
    return user;
  }

  const merged = { ...user };

  if (overrides.createdAt != null && overrides.createdAt !== '') {
    merged['custom:created_at'] = String(overrides.createdAt);
  }

  if (overrides.extendedTrialDays != null) {
    merged['custom:extended_trial_days'] = String(overrides.extendedTrialDays);
  }

  if (overrides.trialExtendedAt != null && overrides.trialExtendedAt !== '') {
    merged['custom:trial_extended_at'] = String(overrides.trialExtendedAt);
  }

  return merged;
};

export const getTrialExpiryTime = user => {
  if (!user?.['custom:created_at']) {
    return 0;
  }

  const createdAt = getCreatedAt(user);
  const extendedAt = getTrialExtendedAt(user);
  const extendedDays = getExtendedDays(user);
  const firstPeriodEnd = createdAt + TRIAL_DAYS * MS_PER_DAY;

  if (extendedAt > 0) {
    const extensionLength = extendedDays > 0 ? extendedDays : EXTENSION_DAYS;
    return extendedAt + extensionLength * MS_PER_DAY;
  }

  if (extendedDays > 0) {
    return firstPeriodEnd + extendedDays * MS_PER_DAY;
  }

  return firstPeriodEnd;
};

export const hasExtendedTrial = user => getTrialExtendedAt(user) > 0 || getExtendedDays(user) > 0;

export const isFirstTrialPeriodExpired = user => {
  if (!user?.['custom:created_at']) {
    return false;
  }

  const createdAt = getCreatedAt(user);
  return createdAt + TRIAL_DAYS * MS_PER_DAY < Date.now();
};

export const canExtendCommercialTrial = (user, trialType) => {
  return trialType === 'commercial' && isFirstTrialPeriodExpired(user) && !hasExtendedTrial(user);
};

/**
 * Check if a user's trial has expired
 * @param {Object} user - User object from Cognito
 * @returns {boolean} - True if trial has expired
 */
export const isTrialExpired = user => {
  if (!user || !user['custom:created_at']) {
    return false;
  }

  return getTrialExpiryTime(user) < Date.now();
};

/**
 * Get days remaining in trial
 * @param {Object} user - User object from Cognito
 * @returns {number} - Days remaining (negative if expired)
 */
export const getTrialDaysRemaining = user => {
  if (!user || !user['custom:created_at']) {
    return 0;
  }

  const msRemaining = getTrialExpiryTime(user) - Date.now();
  return Math.ceil(msRemaining / MS_PER_DAY);
};

/**
 * Check if user has academic approval pending
 * @param {Object} user - User object from Cognito
 * @returns {boolean}
 */
export const hasAcademicPending = user => {
  return user?.['custom:academic_pending'] === 'true';
};

/**
 * Check if user has academic approval (pending payment)
 * @param {Object} user - User object from Cognito
 * @returns {boolean}
 */
export const hasAcademicApproved = user => {
  return user?.['custom:academic_approved'] === 'true';
};
